import { useState, useEffect, useRef, useCallback } from 'react';
import synthPlayer from '../utils/synthPlayer';

/**
 * React hook for SynthPlayer integration
 * Provides a clean interface for components to interact with the unified audio player
 */
export const useSynthPlayer = (options = {}) => {
  // subscribeBeat: when false, this hook instance does NOT hold beat state and
  // does NOT register the per-beat callback. Consumers that only need controls
  // (e.g. App root) pass false so they don't re-render on every beat, which would
  // otherwise cascade a full re-render of the whole tree on each tick.
  const { subscribeBeat = true } = options;
  const [isPlaying, setIsPlaying] = useState(false);
  const [playbackState, setPlaybackState] = useState(synthPlayer.playbackState);
  const [currentBeat, setCurrentBeat] = useState(0);
  const [isDownbeat, setIsDownbeat] = useState(false);
  const [currentChord, setCurrentChord] = useState(null);
  const [state, setState] = useState(synthPlayer.getState());
  
  // Refs to track callback functions to avoid memory leaks
  const playingStateCallbackRef = useRef(null);
  const beatCallbackRef = useRef(null);
  const chordChangeCallbackRef = useRef(null);

  // Initialize and setup callbacks
  useEffect(() => {
    // Setup playing state callback
    playingStateCallbackRef.current = (playing, state) => {
      setIsPlaying(playing);
      setPlaybackState(state || synthPlayer.playbackState);
      setState(synthPlayer.getState());
    };
    
    // Setup beat callback
    beatCallbackRef.current = (beat, downbeat, time) => {
      setCurrentBeat(beat);
      setIsDownbeat(downbeat);
    };
    
    // Setup chord change callback
    chordChangeCallbackRef.current = (chord, time, index) => {
      setCurrentChord(chord);
      setState(synthPlayer.getState());
    };
    
    // Register callbacks
    synthPlayer.onPlayingStateChange(playingStateCallbackRef.current);
    if (subscribeBeat) {
      synthPlayer.onBeat(beatCallbackRef.current);
    }
    synthPlayer.onChordChange(chordChangeCallbackRef.current);
    
    // Initialize the player
    synthPlayer.initialize().catch(error => {
      console.error('[useSynthPlayer] Error initializing SynthPlayer:', error);
    });
    
    // Cleanup function
    return () => {
      if (playingStateCallbackRef.current) {
        synthPlayer.offPlayingStateChange(playingStateCallbackRef.current);
      }
      if (beatCallbackRef.current) {
        synthPlayer.offBeat(beatCallbackRef.current);
      }
      if (chordChangeCallbackRef.current) {
        synthPlayer.offChordChange(chordChangeCallbackRef.current);
      }
    };
  }, []);

  // Remove problematic setInterval that was causing flickering
  // State updates are now handled through callbacks only

  // Memoized control functions
  const play = useCallback(async () => {
    try {
      await synthPlayer.play();
    } catch (error) {
      console.error('[useSynthPlayer] Error starting playback:', error);
    }
  }, []);

  const stop = useCallback(() => {
    try {
      synthPlayer.stop();
    } catch (error) {
      console.error('[useSynthPlayer] Error stopping playback:', error);
    }
  }, []);

  const pause = useCallback(() => {
    try {
      synthPlayer.pause();
    } catch (error) {
      console.error('[useSynthPlayer] Error pausing playback:', error);
    }
  }, []);

  const toggle = useCallback(() => {
    // Use synthPlayer's state directly to avoid dependency on React state
    if (synthPlayer.playbackState === 'playing') {
      pause();
    } else {
      play();
    }
  }, [play, pause]);

  // Metronome controls - removed excessive setState calls to prevent flickering
  const setMetronomeEnabled = useCallback((enabled) => {
    synthPlayer.setMetronomeEnabled(enabled);
  }, []);

  const setMetronomeVisualOnly = useCallback((visualOnly) => {
    synthPlayer.setMetronomeVisualOnly(visualOnly);
  }, []);

  const setMetronomeVolume = useCallback((volume) => {
    synthPlayer.setMetronomeVolume(volume);
  }, []);

  // Timing controls
  const setBPM = useCallback((bpm) => {
    synthPlayer.setBPM(bpm);
  }, []);

  const setTimeSignature = useCallback((timeSignature) => {
    synthPlayer.setTimeSignature(timeSignature);
  }, []);

  const setBarsPerChord = useCallback((bars) => {
    synthPlayer.setBarsPerChord(bars);
  }, []);

  // Progression control
  const setProgression = useCallback((chords) => {
    synthPlayer.setProgression(chords);
  }, []);

  // Infinite random-progression mode (loops = times to repeat each cadence)
  const setRandomMode = useCallback((enabled, pool, loops) => {
    synthPlayer.setRandomMode(enabled, pool, loops);
  }, []);

  // Sound engine control removed (single engine enforced)

  // Tap tempo
  const tap = useCallback(() => {
    const newBPM = synthPlayer.tap();
    return newBPM;
  }, []);

  const resetTap = useCallback(() => {
    synthPlayer.resetTap();
  }, []);

  // Return the hook interface
  return {
    // State
    isPlaying,
    playbackState,
    isPaused: playbackState === 'paused',
    currentBeat,
    isDownbeat,
    currentChord,
    state,
    
    // Playback controls
    play,
    stop,
    pause,
    toggle,
    
    // Metronome controls
    setMetronomeEnabled,
    setMetronomeVisualOnly, 
    setMetronomeVolume,
    
    // Timing controls
    setBPM,
    setTimeSignature,
    setBarsPerChord,
    
    // Progression control
    setProgression,
    setRandomMode,

    // Tap tempo
    tap,
    resetTap,
    
    // Direct access to player (for advanced use cases)
    synthPlayer
  };
};

export default useSynthPlayer;